import {
  useAlarmGroups,
  useLocationIncidents,
} from '@hakimo-ui/hakimo/data-access';
import {
  dismissToast,
  escalationAudioNotifyAtom,
  hasPermission,
  toast,
  useEscalationSoundAlert,
  useUser,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

const RG_ERROR_TOAST_ID = 'escalation-alert-rg-error';
const SCAN_ERROR_TOAST_ID = 'escalation-alert-scan-error';

const POLL_INTERVAL = 30000;

const PAGE_SIZE = 15;

const RG_PARAMS = (() => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('pageSize', String(PAGE_SIZE));
  params.set('statuses', 'active');
  params.set('relativeTimeHours', '168');
  return params.toString();
})();

const SCAN_PARAMS = (() => {
  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('pageSize', String(PAGE_SIZE));
  params.set('state', 'In Progress');
  params.set('relativeTimeMinutes', '1440');
  return params.toString();
})();

function useFetchErrorAlert(isError: boolean, toastId: string, label: string) {
  useEffect(() => {
    if (isError) {
      toast(`Escalation alerts for ${label} may not be live - retrying`, {
        type: 'error',
        id: toastId,
      });
    } else {
      dismissToast(toastId);
    }
  }, [isError, toastId, label]);
}

function GlobalEscalationAlertComponent() {
  const user = useUser();
  const audioNotify = useAtomValue(escalationAudioNotifyAtom);

  const canViewRg = hasPermission(user, 'location_incidents:view');
  const canViewScan = hasPermission(user, 'alarm_group:view');

  const { data: rgData, isError: isRgError } = useLocationIncidents(
    RG_PARAMS,
    POLL_INTERVAL,
    undefined,
    !(audioNotify && canViewRg)
  );
  const { data: scanData, isError: isScanError } = useAlarmGroups(
    SCAN_PARAMS,
    POLL_INTERVAL,
    undefined,
    !(audioNotify && canViewScan)
  );

  useFetchErrorAlert(isRgError, RG_ERROR_TOAST_ID, 'Remote Guarding');
  useFetchErrorAlert(isScanError, SCAN_ERROR_TOAST_ID, 'Scan');

  const rgIds = !canViewRg
    ? []
    : rgData?.items?.flatMap((item) =>
        item.id !== undefined ? [`rg-${item.id}`] : []
      );
  const scanIds = !canViewScan
    ? []
    : scanData?.items?.map((item) => `scan-${item.id}`);

  useEscalationSoundAlert(rgIds, '-rg');
  useEscalationSoundAlert(scanIds, '-scan');

  return null;
}

export const GlobalEscalationAlert = withErrorBoundary(
  GlobalEscalationAlertComponent,
  // eslint-disable-next-line react/jsx-no-useless-fragment -- deliberately empty: this fallback must render nothing
  <></>
);

export default GlobalEscalationAlert;
